// HM-333+334 cluster 16: ListiniSection completa SPEC mockup A.
// Layout 2 colonne: lista listini sx 360px + dettaglio dx con form 6
// sezioni (tariffe / pernotto / regole / ore extra / preview / camere).

import { Button, toast } from '@hotel-manager/ui/components';
import { BedDouble, CalendarClock, Plus, Trash2, Wallet, X } from '@hotel-manager/ui/icons';
import { cn } from '@hotel-manager/ui/lib/cn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  type PriceCalcResult,
  type PricingRule,
  type RoomTypeFull,
  duplicatePricingRule,
  listPricingRules,
  listRoomTypesFull,
  previewCalc,
  updatePricingRule,
} from '../../../api/pricing.js';
import { fetchRooms } from '../../../api/rooms.js';

export function ListiniSection() {
  const queryClient = useQueryClient();
  const rulesQuery = useQuery({ queryKey: ['pricing-rules'], queryFn: listPricingRules });
  const roomTypesQuery = useQuery({ queryKey: ['room-types'], queryFn: listRoomTypesFull });
  const roomsQuery = useQuery({ queryKey: ['rooms'], queryFn: fetchRooms });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && rulesQuery.data?.pricingRules[0]) {
      setSelectedId(rulesQuery.data.pricingRules[0].id);
    }
  }, [rulesQuery.data, selectedId]);

  const selected = rulesQuery.data?.pricingRules.find((r) => r.id === selectedId) ?? null;
  const roomTypesByPricingRule = useMemo(() => {
    const map = new Map<string, RoomTypeFull[]>();
    for (const rt of roomTypesQuery.data?.roomTypes ?? []) {
      if (!rt.pricingRuleId) continue;
      const arr = map.get(rt.pricingRuleId) ?? [];
      arr.push(rt);
      map.set(rt.pricingRuleId, arr);
    }
    return map;
  }, [roomTypesQuery.data]);

  const roomsByRoomTypeId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of roomsQuery.data?.rooms ?? []) {
      const arr = map.get(r.roomTypeId) ?? [];
      arr.push(r.code);
      map.set(r.roomTypeId, arr);
    }
    return map;
  }, [roomsQuery.data]);

  const duplicateMutation = useMutation({
    mutationFn: duplicatePricingRule,
    onSuccess: () => {
      toast.success('Listino duplicato');
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
    },
    onError: () => toast.error('Errore duplicazione'),
  });

  if (rulesQuery.isLoading) {
    return <p className="text-text-secondary">Caricamento listini…</p>;
  }
  if (rulesQuery.error) {
    return (
      <div className="rounded-md p-3 text-sm bg-state-guas/10 text-state-guas border border-state-guas/30">
        Errore: {(rulesQuery.error as Error).message}
      </div>
    );
  }

  const rules = rulesQuery.data?.pricingRules ?? [];
  const roomCodesForSelected = (() => {
    if (!selected) return [];
    const types = roomTypesByPricingRule.get(selected.id) ?? [];
    const codes: string[] = [];
    for (const t of types) {
      codes.push(...(roomsByRoomTypeId.get(t.id) ?? []));
    }
    return codes.sort();
  })();

  return (
    <div className="grid gap-6 grid-cols-1 lg:grid-cols-[360px_1fr]" data-testid="listini-section">
      {/* Lista listini */}
      <div className="flex flex-col gap-2.5" data-testid="listini-list">
        {rules.map((r) => {
          const types = roomTypesByPricingRule.get(r.id) ?? [];
          const roomCount = types.reduce(
            (acc, t) => acc + (roomsByRoomTypeId.get(t.id)?.length ?? 0),
            0,
          );
          const tipoLabel = types[0]?.label ?? '—';
          return (
            <ListinoCard
              key={r.id}
              rule={r}
              tipoLabel={tipoLabel}
              roomCount={roomCount}
              active={r.id === selectedId}
              onClick={() => setSelectedId(r.id)}
            />
          );
        })}
        <button
          type="button"
          className="bg-bg-card border border-dashed border-border-input rounded-xl p-4 text-text-secondary text-sm font-medium flex items-center justify-center gap-2 hover:bg-bg-base hover:border-accent-primary hover:text-accent-primary-text transition-colors"
          data-testid="add-listino"
          onClick={() => toast.info('Creazione listino in arrivo')}
        >
          <Plus size={16} />
          Crea nuovo listino
        </button>
      </div>

      {/* Dettaglio */}
      {selected && (
        <ListinoDetail
          rule={selected}
          roomCodes={roomCodesForSelected}
          tipoLabel={roomTypesByPricingRule.get(selected.id)?.[0]?.label ?? '—'}
          onDuplicate={() => duplicateMutation.mutate(selected.id)}
        />
      )}
    </div>
  );
}

// --------------------------- ListinoCard ---------------------------

function ListinoCard({
  rule,
  tipoLabel,
  roomCount,
  active,
  onClick,
}: {
  rule: PricingRule;
  tipoLabel: string;
  roomCount: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`listino-card-${rule.id}`}
      data-active={active}
      className={cn(
        'text-left bg-bg-card border-[1.5px] border-border-base rounded-xl p-4 transition-all',
        'hover:border-border-input hover:-translate-y-px hover:shadow-hm-sm',
        active && '!border-accent-primary bg-accent-soft shadow-[0_4px_16px_rgba(233,123,61,0.15)]',
      )}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[15px] font-bold text-text-primary">{rule.name}</span>
        <span
          className={cn(
            'text-[10px] font-semibold px-2 py-[3px] rounded-md',
            active ? 'bg-accent-primary text-text-on-accent' : 'bg-bg-muted text-text-secondary',
          )}
        >
          {tipoLabel}
        </span>
      </div>
      <div className="text-xs text-text-secondary flex items-center gap-3">
        <span className="inline-flex items-center gap-1">
          <CalendarClock size={13} className="text-text-muted" />
          Da € {rule.base2hPrice}/2h
        </span>
        <span className="inline-flex items-center gap-1">
          <Wallet size={13} className="text-text-muted" />
          Pernotto € {rule.overnightPrice}
        </span>
      </div>
      <div className="text-[11px] text-text-muted mt-2">{roomCount} camere assegnate</div>
    </button>
  );
}

// --------------------------- ListinoDetail ---------------------------

interface FormDraft {
  name: string;
  base2hPrice: number;
  hour3Supplement: number;
  hour4plusUnit: number;
  overnightPrice: number;
  weekendEnabled: boolean;
  weekendMaxHours: number;
  weekendSurchargePct: number;
  holidayEnabled: boolean;
  holidayMaxHours: number;
  holidaySurchargePct: number;
  bufferMinutes: number;
  roundingMode: 'full_hour' | 'half_hour';
  overrideRole: 'admin_only' | 'admin_reception';
}

function ruleToDraft(r: PricingRule): FormDraft {
  return {
    name: r.name,
    base2hPrice: Number(r.base2hPrice),
    hour3Supplement: Number(r.hour3Supplement),
    hour4plusUnit: Number(r.hour4plusUnit),
    overnightPrice: Number(r.overnightPrice),
    weekendEnabled: r.weekendMaxHours != null,
    weekendMaxHours: r.weekendMaxHours ?? 3,
    weekendSurchargePct: r.weekendSurchargePct,
    holidayEnabled: r.holidayMaxHours != null,
    holidayMaxHours: r.holidayMaxHours ?? 3,
    holidaySurchargePct: r.holidaySurchargePct,
    bufferMinutes: r.bufferMinutes,
    roundingMode: r.roundingMode,
    overrideRole: r.overrideRole,
  };
}

function ListinoDetail({
  rule,
  roomCodes,
  tipoLabel,
  onDuplicate,
}: {
  rule: PricingRule;
  roomCodes: string[];
  tipoLabel: string;
  onDuplicate: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FormDraft>(() => ruleToDraft(rule));
  const [dirty, setDirty] = useState(false);

  // Reset draft quando cambia la rule selezionata
  useEffect(() => {
    setDraft(ruleToDraft(rule));
    setDirty(false);
  }, [rule]);

  function patch<K extends keyof FormDraft>(k: K, v: FormDraft[K]) {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  }

  const saveMutation = useMutation({
    mutationFn: () =>
      updatePricingRule(rule.id, {
        name: draft.name,
        base2hPrice: draft.base2hPrice,
        hour3Supplement: draft.hour3Supplement,
        hour4plusUnit: draft.hour4plusUnit,
        overnightPrice: draft.overnightPrice,
        weekendMaxHours: draft.weekendEnabled ? draft.weekendMaxHours : null,
        weekendSurchargePct: draft.weekendSurchargePct,
        holidayMaxHours: draft.holidayEnabled ? draft.holidayMaxHours : null,
        holidaySurchargePct: draft.holidaySurchargePct,
        bufferMinutes: draft.bufferMinutes,
        roundingMode: draft.roundingMode,
        overrideRole: draft.overrideRole,
      }),
    onSuccess: () => {
      toast.success('Listino salvato');
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['pricing-rules'] });
    },
    onError: () => toast.error('Errore salvataggio'),
  });

  return (
    <article
      className="bg-bg-card border border-border-base rounded-xl overflow-hidden flex flex-col"
      data-testid="listino-detail"
    >
      {/* Header */}
      <header className="px-6 py-5 border-b border-border-base flex items-start justify-between bg-gradient-to-b from-bg-base to-bg-card">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-[10px] bg-accent-soft flex items-center justify-center">
            <Wallet size={22} className="text-accent-primary" />
          </span>
          <div>
            <h2 className="text-lg font-bold text-text-primary">{rule.name}</h2>
            <p className="text-xs text-text-secondary mt-0.5">
              Tipologia: {tipoLabel} · Applicato a {roomCodes.length} camere · Modificato{' '}
              {new Date(rule.updatedAt).toLocaleDateString('it-IT', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onDuplicate}
            data-testid="action-duplicate"
            title="Duplica"
            className="bg-bg-card border border-border-base w-9 h-9 rounded-lg flex items-center justify-center text-text-secondary hover:bg-bg-base"
          >
            <Plus size={16} />
          </button>
          <button
            type="button"
            data-testid="action-delete"
            title="Elimina"
            className="bg-bg-card border border-border-base w-9 h-9 rounded-lg flex items-center justify-center text-text-secondary hover:bg-state-guas/10 hover:border-state-guas hover:text-state-guas"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="px-6 py-6 space-y-7">
        {/* Sezione 1: Tariffe base */}
        <Section title="Tariffe base (giorni feriali)" icon={CalendarClock}>
          <TariffaRow
            chip="2h"
            label="Tariffa base · prime 2 ore"
            value={draft.base2hPrice}
            onChange={(v) => patch('base2hPrice', v)}
          />
          <TariffaRow
            chip="3a"
            label="3ª ora · supplemento"
            value={draft.hour3Supplement}
            onChange={(v) => patch('hour3Supplement', v)}
          />
          <TariffaRow
            chip="4a+"
            label="Dalla 4ª ora in poi · per ogni ora"
            value={draft.hour4plusUnit}
            onChange={(v) => patch('hour4plusUnit', v)}
          />
        </Section>

        {/* Sezione 2: Pernottamento */}
        <Section title="Pernottamento" icon={Wallet}>
          <div
            className="rounded-[10px] border border-accent-soft p-4 flex items-center justify-between"
            style={{
              background: 'linear-gradient(135deg, #FCF1EA 0%, #FAF7F2 100%)',
            }}
          >
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-lg bg-bg-card flex items-center justify-center">
                <Wallet size={20} className="text-state-ris" />
              </span>
              <div>
                <div className="text-[13px] font-semibold text-text-primary">
                  Tariffa pernottamento (cap massimo)
                </div>
                <div className="text-[11px] text-text-secondary mt-0.5">
                  Sistema applica la più economica tra ore calcolate e questa tariffa
                </div>
              </div>
            </div>
            <PriceInput
              value={draft.overnightPrice}
              onChange={(v) => patch('overnightPrice', v)}
              testid="input-overnight"
              width="130px"
            />
          </div>
        </Section>

        {/* Sezione 3: Regole speciali */}
        <Section title="Regole speciali" icon={CalendarClock}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <RuleCard
              title="Sab/Dom"
              enabled={draft.weekendEnabled}
              onToggle={(v) => patch('weekendEnabled', v)}
              testid="rule-weekend"
            >
              <RuleRow label="Cap massimo ore" suffix="ore">
                <MiniInput
                  value={draft.weekendMaxHours}
                  onChange={(v) => patch('weekendMaxHours', v)}
                />
              </RuleRow>
              <RuleRow label="Maggiorazione" suffix="%">
                <MiniInput
                  value={draft.weekendSurchargePct}
                  onChange={(v) => patch('weekendSurchargePct', v)}
                />
              </RuleRow>
            </RuleCard>

            <RuleCard
              title="Festivi"
              enabled={draft.holidayEnabled}
              onToggle={(v) => patch('holidayEnabled', v)}
              testid="rule-holiday"
            >
              <RuleRow label="Maggiorazione" suffix="%">
                <MiniInput
                  value={draft.holidaySurchargePct}
                  onChange={(v) => patch('holidaySurchargePct', v)}
                />
              </RuleRow>
              <RuleRow label="Cap ore" suffix="ore">
                <MiniInput
                  value={draft.holidayMaxHours}
                  onChange={(v) => patch('holidayMaxHours', v)}
                />
              </RuleRow>
            </RuleCard>
          </div>
        </Section>

        {/* Sezione 4: Ore extra automatiche */}
        <Section title="Ore extra automatiche" icon={CalendarClock}>
          <div className="bg-bg-card border border-border-base rounded-xl p-4 space-y-3">
            <RuleRow label="Cuscinetto tolleranza dopo orario uscita" suffix="minuti">
              <MiniInput value={draft.bufferMinutes} onChange={(v) => patch('bufferMinutes', v)} />
            </RuleRow>
            <RuleRow label="Arrotondamento ora extra">
              <select
                className="border border-border-input rounded-md px-2.5 py-1.5 text-[13px] bg-bg-card outline-none focus:border-accent-primary w-[140px]"
                value={draft.roundingMode}
                onChange={(e) => patch('roundingMode', e.target.value as 'full_hour' | 'half_hour')}
                data-testid="select-rounding"
              >
                <option value="full_hour">1 ora piena</option>
                <option value="half_hour">30 minuti</option>
              </select>
            </RuleRow>
            <RuleRow label="Override ora extra al check-out">
              <select
                className="border border-border-input rounded-md px-2.5 py-1.5 text-[13px] bg-bg-card outline-none focus:border-accent-primary w-[150px]"
                value={draft.overrideRole}
                onChange={(e) =>
                  patch('overrideRole', e.target.value as 'admin_only' | 'admin_reception')
                }
                data-testid="select-override-role"
              >
                <option value="admin_only">Solo admin</option>
                <option value="admin_reception">Admin + reception</option>
              </select>
            </RuleRow>
          </div>
        </Section>

        {/* Sezione 5: Preview calcolo */}
        <PreviewSection rule={rule} draft={draft} />

        {/* Sezione 6: Camere assegnate */}
        <Section title={`Camere assegnate (${roomCodes.length})`} icon={BedDouble}>
          {roomCodes.length === 0 ? (
            <p className="text-sm text-text-muted italic">Nessuna camera assegnata</p>
          ) : (
            <div className="bg-bg-base border border-border-base rounded-lg p-3 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-text-secondary mr-1">Hotel Mik:</span>
              {roomCodes.map((c) => (
                <span
                  key={c}
                  className="bg-bg-card border border-border-input px-2.5 py-1 rounded-md text-xs font-semibold text-text-primary"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Footer */}
      <footer className="px-6 py-4 bg-bg-base border-t border-border-base flex justify-end gap-2">
        <Button
          variant="secondary"
          disabled={!dirty}
          onClick={() => {
            setDraft(ruleToDraft(rule));
            setDirty(false);
          }}
          data-testid="btn-cancel"
        >
          Annulla modifiche
        </Button>
        <Button
          variant="primary"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          data-testid="btn-save"
        >
          {saveMutation.isPending ? 'Salvataggio…' : 'Salva listino'}
        </Button>
      </footer>
    </article>
  );
}

// --------------------------- helpers UI ---------------------------

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof CalendarClock;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-[11px] font-bold text-accent-primary-text uppercase tracking-[0.8px] mb-3.5 flex items-center gap-1.5">
        <Icon size={14} />
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function TariffaRow({
  chip,
  label,
  value,
  onChange,
}: {
  chip: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div
      className="grid grid-cols-[60px_1fr_120px_40px] gap-3 items-center px-3.5 py-2.5 bg-bg-base border border-border-base rounded-lg"
      data-testid={`tariffa-${chip.toLowerCase()}`}
    >
      <span className="bg-accent-primary text-text-on-accent rounded-md px-2.5 py-1.5 text-center text-[13px] font-bold">
        {chip}
      </span>
      <span className="text-[13px] text-text-primary font-medium">{label}</span>
      <PriceInput value={value} onChange={onChange} />
      <span />
    </div>
  );
}

function PriceInput({
  value,
  onChange,
  testid,
  width = 'auto',
}: {
  value: number;
  onChange: (v: number) => void;
  testid?: string;
  width?: string;
}) {
  return (
    <div className="relative" style={{ width }}>
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary text-sm font-semibold">
        €
      </span>
      <input
        type="number"
        step="0.5"
        min="0"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="pl-7 pr-3 py-2 border border-border-input rounded-md text-sm font-bold text-text-primary bg-bg-card w-full text-right outline-none focus:border-accent-primary focus:ring-2 focus:ring-accent-soft"
      />
    </div>
  );
}

function RuleCard({
  title,
  enabled,
  onToggle,
  children,
  testid,
}: {
  title: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  children: React.ReactNode;
  testid?: string;
}) {
  return (
    <div
      className="bg-bg-card border border-border-base rounded-[10px] p-4"
      data-testid={testid}
      data-enabled={enabled}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold text-text-primary flex items-center gap-2">
          {title}
        </div>
        <Switch on={enabled} onToggle={() => onToggle(!enabled)} />
      </div>
      {enabled && <div className="space-y-2.5">{children}</div>}
    </div>
  );
}

function Switch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      className={cn(
        'relative w-[38px] h-[22px] rounded-full transition-colors',
        on ? 'bg-accent-primary' : 'bg-border-base',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 w-[18px] h-[18px] bg-bg-card rounded-full shadow transition-all',
          on ? 'left-[18px]' : 'left-0.5',
        )}
      />
    </button>
  );
}

function RuleRow({
  label,
  suffix,
  children,
}: {
  label: string;
  suffix?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 text-[13px]">
      <span className="text-text-secondary flex-1">{label}</span>
      {children}
      {suffix && <span className="text-text-secondary text-xs">{suffix}</span>}
    </div>
  );
}

function MiniInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      min="0"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-[70px] px-2 py-1 border border-border-input rounded-md text-[13px] text-center bg-bg-card outline-none focus:border-accent-primary"
    />
  );
}

// --------------------------- Preview Section ---------------------------

function PreviewSection({ rule, draft }: { rule: PricingRule; draft: FormDraft }) {
  const [calc, setCalc] = useState<PriceCalcResult | null>(null);
  const [hours, setHours] = useState(4);

  // Debounced fetch preview-calc
  // HM-FIX-16.3 BUG2: backend Zod CreateBodySchema vuole NUMBER per i
  // prezzi (transform a string interno). Passare .toFixed(2) come
  // string causava 400 invalid_body silenziosamente -> stuck loading.
  useEffect(() => {
    const timer = setTimeout(() => {
      previewCalc({
        rule: {
          base2hPrice: draft.base2hPrice,
          hour3Supplement: draft.hour3Supplement,
          hour4plusUnit: draft.hour4plusUnit,
          overnightPrice: draft.overnightPrice,
          weekendMaxHours: draft.weekendEnabled ? draft.weekendMaxHours : null,
          weekendSurchargePct: draft.weekendSurchargePct,
          holidayMaxHours: draft.holidayEnabled ? draft.holidayMaxHours : null,
          holidaySurchargePct: draft.holidaySurchargePct,
          name: draft.name,
        },
        hours,
        date: new Date().toISOString(),
      })
        .then(setCalc)
        .catch(() => setCalc(null));
    }, 250);
    return () => clearTimeout(timer);
  }, [draft, hours]);

  return (
    <section data-testid="preview-section">
      <h3 className="text-[11px] font-bold text-accent-primary-text uppercase tracking-[0.8px] mb-3.5 flex items-center gap-1.5">
        Anteprima calcolo (esempio: cliente {hours}h,{' '}
        {new Date().toLocaleDateString('it-IT', { weekday: 'long' })})
      </h3>
      <div
        className="rounded-xl border border-[#C5DBB8] p-5"
        style={{ background: 'linear-gradient(135deg, #F4FAF1 0%, #FAF7F2 100%)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-[#5F8A50] uppercase tracking-[0.6px]">
            Simulazione
          </span>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <button
              type="button"
              onClick={() => setHours((h) => Math.max(1, h - 1))}
              className="w-6 h-6 rounded-full bg-bg-card border border-border-input text-text-primary hover:border-accent-primary"
              data-testid="preview-hours-dec"
            >
              −
            </button>
            <span className="font-semibold tabular-nums w-8 text-center">{hours}h</span>
            <button
              type="button"
              onClick={() => setHours((h) => Math.min(24, h + 1))}
              className="w-6 h-6 rounded-full bg-bg-card border border-border-input text-text-primary hover:border-accent-primary"
              data-testid="preview-hours-inc"
            >
              +
            </button>
          </div>
        </div>
        {calc ? (
          <div className="text-[13px]" data-testid="preview-result">
            {calc.breakdown.map((b) => (
              <div
                key={b.label}
                className="grid grid-cols-[80px_1fr_80px] gap-3 py-2 items-center border-b border-dashed border-[#C5DBB8] last:border-none"
              >
                <span className="text-[#5F8A50] font-semibold">
                  {b.label.split('·')[0]?.trim() ?? ''}
                </span>
                <span className="text-text-secondary text-xs">{b.label}</span>
                <span className="text-right text-text-primary font-semibold tabular-nums">
                  € {b.amount.toFixed(2)}
                </span>
              </div>
            ))}
            <div className="grid grid-cols-[80px_1fr_80px] gap-3 py-3 items-center font-bold">
              <span />
              <span className="text-text-primary">Totale {hours}h</span>
              <span className="text-right text-accent-primary-text text-lg tabular-nums">
                € {calc.total.toFixed(2)}
              </span>
            </div>
            {calc.appliedCap !== 'none' && (
              <div className="text-[11px] text-accent-primary-text mt-2 italic">
                Cap applicato: {calc.appliedCap}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-text-muted">Calcolo in corso…</p>
        )}
      </div>
    </section>
  );
}
// X import preserved for potential future use (close icons in row remove)
void X;
